import React, { useState } from 'react';
import { Plus, Trash2, AlertOctagon, SquarePen } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import { differenceInDays } from 'date-fns';
import DeleteConfirmDialog from '../DeleteConfirmDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Add Suspension Dialog
export const AddSuspensionDialog = ({ open, onOpenChange, employee, onSuccess }) => {
  const [suspensionData, setSuspensionData] = useState({ from_date: '', to_date: '', reason: '' });
  const [suspensionDays, setSuspensionDays] = useState(0);

  const handleDateChange = (field, value) => {
    const newData = { ...suspensionData, [field]: value };
    setSuspensionData(newData);
    
    if (newData.from_date && newData.to_date) {
      const days = differenceInDays(new Date(newData.to_date), new Date(newData.from_date)) + 1;
      setSuspensionDays(days > 0 ? days : 0);
    } else {
      setSuspensionDays(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employee || suspensionDays <= 0) {
      toast({ title: 'Error', description: 'Invalid date range', variant: 'destructive' });
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/attendance/suspension`, {
        employee_id: employee.id,
        from_date: suspensionData.from_date,
        to_date: suspensionData.to_date,
        reason: suspensionData.reason
      });
      
      toast({ 
        title: 'Success', 
        description: `Suspension of ${suspensionDays} days added for ${employee.name}. This will be reflected in LOP.` 
      });
      
      setSuspensionData({ from_date: '', to_date: '', reason: '' });
      setSuspensionDays(0);
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add suspension', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-orange-600" />
            Add Suspension - {employee?.name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
            Suspension days will be added to LOP (Loss of Pay) and deducted from the employee's salary.
          </div>
          <div>
            <Label>From Date</Label>
            <Input 
              type="date" 
              required
              value={suspensionData.from_date}
              onChange={(e) => handleDateChange('from_date', e.target.value)}
            />
          </div>
          <div>
            <Label>To Date</Label>
            <Input 
              type="date" 
              required
              value={suspensionData.to_date}
              onChange={(e) => handleDateChange('to_date', e.target.value)}
            />
          </div>
          {suspensionDays > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 font-semibold">
                Total Suspension Days: {suspensionDays} {suspensionDays === 1 ? 'day' : 'days'}
              </p>
            </div>
          )}
          <div>
            <Label>Reason</Label>
            <Input 
              value={suspensionData.reason}
              onChange={(e) => setSuspensionData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Reason for suspension (optional)"
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={suspensionDays <= 0} className="bg-orange-600 hover:bg-orange-700">
              Add Suspension
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Manage Suspensions Dialog
export const ManageSuspensionsDialog = ({ open, onOpenChange, employee, onSuccess, onAddNew }) => {
  const [suspensions, setSuspensions] = useState([]);
  const [editingSuspension, setEditingSuspension] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [suspensionToDelete, setSuspensionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role;

  React.useEffect(() => {
    if (open && employee) {
      loadSuspensions();
    }
  }, [open, employee]);

  const loadSuspensions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/attendance/suspension/${employee.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuspensions(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load suspensions', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (suspension) => {
    setSuspensionToDelete(suspension);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteSuspension = async () => {
    if (!suspensionToDelete) return;
    setDeleting(true);
    
    try {
      await axios.delete(`${API_URL}/api/attendance/suspension/${suspensionToDelete.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Suspension deleted successfully' });
      loadSuspensions();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete suspension', variant: 'destructive' });
    } finally {
      setDeleting(false);
      setSuspensionToDelete(null);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingSuspension) return;
    
    try {
      await axios.put(`${API_URL}/api/attendance/suspension/${editingSuspension.id}`, {
        from_date: editingSuspension.from_date,
        to_date: editingSuspension.to_date,
        reason: editingSuspension.reason
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Suspension updated successfully' });
      setEditingSuspension(null);
      loadSuspensions();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update suspension', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-orange-600" />
            Manage Suspensions - {employee?.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : suspensions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No suspension records found for this employee
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {suspensions.map((susp) => (
                <div key={susp.id} className="border rounded-lg p-4 bg-orange-50">
                  {editingSuspension?.id === susp.id ? (
                    <form onSubmit={handleUpdate} className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>From Date</Label>
                          <Input
                            type="date"
                            value={editingSuspension.from_date?.split('T')[0]}
                            onChange={(e) => setEditingSuspension(prev => ({ ...prev, from_date: e.target.value }))}
                            required
                          />
                        </div>
                        <div>
                          <Label>To Date</Label>
                          <Input
                            type="date"
                            value={editingSuspension.to_date?.split('T')[0]}
                            onChange={(e) => setEditingSuspension(prev => ({ ...prev, to_date: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Reason</Label>
                        <Input
                          value={editingSuspension.reason || ''}
                          onChange={(e) => setEditingSuspension(prev => ({ ...prev, reason: e.target.value }))}
                          placeholder="Reason for suspension"
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button type="button" variant="outline" size="sm" onClick={() => setEditingSuspension(null)}>
                          Cancel
                        </Button>
                        <Button type="submit" size="sm" className="bg-orange-600 hover:bg-orange-700">
                          Save Changes
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="bg-orange-100 text-orange-800">
                            {susp.days} {susp.days === 1 ? 'day' : 'days'}
                          </Badge>
                          <span className="text-sm text-gray-600">
                            {new Date(susp.from_date).toLocaleDateString('en-GB')} - {new Date(susp.to_date).toLocaleDateString('en-GB')}
                          </span>
                        </div>
                        {susp.reason && (
                          <p className="text-sm text-gray-700">Reason: {susp.reason}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Created: {new Date(susp.created_at).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                      {userRole !== 'viewer' && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingSuspension({...susp})}
                            data-testid={`edit-suspension-${susp.id}`}
                          >
                            <SquarePen className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(susp)}
                            data-testid={`delete-suspension-${susp.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <div className="flex justify-between pt-4 border-t">
            {userRole !== 'viewer' && (
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  onAddNew(employee);
                }}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
                data-testid="add-new-suspension-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New Suspension
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>

          {/* Delete Suspension Confirmation Dialog */}
          <DeleteConfirmDialog
            open={deleteConfirmOpen}
            onOpenChange={setDeleteConfirmOpen}
            title="Delete Suspension"
            description="Are you sure you want to delete this suspension? This will reduce the employee's LOP days."
            itemName={suspensionToDelete ? `${suspensionToDelete.days || 0} days suspension` : ''}
            onConfirm={confirmDeleteSuspension}
            loading={deleting}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
